/* eslint-disable react-hooks/exhaustive-deps */
import { css, cx } from "@emotion/css";
import { HandWaveIcon, ExclamationCircleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, fontSize, fontWeight, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { LicenseMessageDisposition, HostingEnvironment } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import pluralize from "pluralize";
import * as React from "react";
import { useState } from "react";
import { useAnalyticLicenseUsageDispatch } from "~/analytics/Analytics";
import { useSpaceAwareNavigation } from "../Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
export type LicenseMessageUrgency = "info" | "attention" | "danger";
export function GetLicenseExpirationUrgency(licenseDisposition: LicenseMessageDisposition | undefined): LicenseMessageUrgency {
    switch (licenseDisposition) {
        case LicenseMessageDisposition.Information:
            return "info";
        // trial expires soon
        case LicenseMessageDisposition.Notice:
            return "attention";
        // trial expires today (if remainingDays is 0) or in grace period
        case LicenseMessageDisposition.Warning:
        case LicenseMessageDisposition.Error:
            return "danger";
        default:
            return "info";
    }
}
export function LicenseCountdownBanner() {
    const [bannerState, setBannerState] = useState<LicenseMessageUrgency>("info");
    const dispatchAction = useAnalyticLicenseUsageDispatch();
    const navigation = useSpaceAwareNavigation();
    const { result: licenseStatus } = useInlineStatusQuery(async (repository) => {
        const licenseStatus = await repository.Licenses.getCurrentStatus();
        const licenseDisposition = licenseStatus.Messages[0]?.Disposition;
        const bannerState = GetLicenseExpirationUrgency(licenseDisposition);
        setBannerState(bannerState);
        return licenseStatus;
    }, [], "Get license current status");
    const onBannerClicked = () => {
        dispatchAction("Click Countdown Banner", { urgency: bannerState, resource: "License" });
        navigation.navigate(links.licenseUsageDashboardPage.generateUrl());
    };
    if (licenseStatus?.DaysToEffectiveExpiryDate === undefined || !licenseStatus?.IsTrial || licenseStatus.HostingEnvironment !== HostingEnvironment.OctopusCloud) {
        return null;
    }
    return (<div className={cx(styles.bannerContainer, styles.bannerState[bannerState])} onClick={onBannerClicked}>
            {bannerState === "attention" || bannerState === "danger" ? <ExclamationCircleIcon size={20}/> : <HandWaveIcon size={20}/>}

            {/* license is not in grace period and not expired */}
            {licenseStatus.DaysToEffectiveExpiryDate >= 0 ? (<div>
                    {/* trial expires today (if remainingDays is 0) */}
                    Your cloud trial ends in <b>{pluralize("day", licenseStatus.DaysToEffectiveExpiryDate + 1, true)}</b>
                </div>) : (<div>Your cloud trial has expired</div>)}
            <div className={styles.licenseUsage}>View License Usage</div>
        </div>);
}
const styles = {
    bannerContainer: css({
        minHeight: "2.25rem",
        display: "flex",
        flexWrap: "wrap",
        padding: `${space[8]} ${space[12]}`,
        justifyContent: "center",
        gap: space[6],
        borderRadius: borderRadius["small"],
        cursor: "pointer",
        fontSize: fontSize["medium"],
        "& b": {
            fontWeight: fontWeight["700"],
        },
    }),
    bannerState: {
        info: css({
            backgroundColor: themeTokens.color.callout.background.info.default,
            "& svg": {
                fill: themeTokens.color.callout.icon.default,
            },
            "&:hover": {
                backgroundColor: themeTokens.color.callout.background.info.hover,
            },
            "&:active": {
                backgroundColor: themeTokens.color.callout.background.info.pressed,
            },
        }),
        attention: css({
            backgroundColor: themeTokens.color.callout.background.warning.default,
            "& svg": {
                fill: themeTokens.color.callout.icon.warning,
            },
            "&:hover": {
                backgroundColor: themeTokens.color.callout.background.warning.hover,
            },
            "&:active": {
                backgroundColor: themeTokens.color.callout.background.warning.pressed,
            },
        }),
        danger: css({
            backgroundColor: themeTokens.color.callout.background.danger.default,
            "& svg": {
                fill: themeTokens.color.callout.icon.danger,
            },
            "&:hover": {
                backgroundColor: themeTokens.color.callout.background.danger.hover,
            },
            "&:active": {
                backgroundColor: themeTokens.color.callout.background.danger.pressed,
            },
        }),
    },
    licenseUsage: css({
        color: themeTokens.color.text.link.default,
        fontSize: fontSize["medium"],
        fontWeight: fontWeight["700"],
    }),
};
